/**
 * Monitoreo › Dominio — estado DMARC / SPF / DNS de los dominios cucuru.
 *
 * Backend: lambda `dominio` de webadmin2 (GET /dominio, GET /dominio/{d},
 * GET /dominio/{d}/historico, POST /dominio/{d}/reevaluar). Sólo lee la tabla
 * `webadmin2_dominio` que escribe el stack DominioMonitor
 * (dominio-monitor/ en este repo — los tipos de acá espejan su SCHEMA.md).
 * La única acción es "reevaluar": invoca el evaluator y los datos se
 * actualizan a los ~20 s.
 */
import { authFetch, authHeader, jsonOrThrow, qsFrom } from "./monitoring";

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "http://localhost:3000";

// ---------- Tipos (SCHEMA.md: status#latest) ----------
export type ManagedIn      = "squarespace" | "aws" | "otro";
export type DmarcPolicy    = "none" | "quarantine" | "reject";
export type Severity       = "critical" | "high" | "medium" | "low" | "info";
export type FindingWhere   = "squarespace" | "aws" | "externo" | "n/a";
export type FindingStatus  = "open" | "accepted" | "deferred" | "out_of_scope";
export type SourceClass    = "ok" | "parcial" | "autorizado_sin_alinear" | "google_sin_spf" | "no_autorizado";

export interface Usage {
  receives_mail:       boolean;
  sends_mail_observed: boolean;
  senders_in_spf:      boolean;
}

export interface Soa {
  present:          boolean;
  mname?:           string | null;
  rname?:           string | null;
  serial?:          number | null;
  refresh?:         number | null;
  retry?:           number | null;
  expire?:          number | null;
  minimum?:         number | null;
  serial_format_ok: boolean;
  expire_ok:        boolean;
}

export interface Mx {
  priority:  number;
  host:      string;
  ip?:       string | null;
  ptr?:      string | null;
  is_google: boolean;
  is_null:   boolean;
}

export interface Spf {
  record:     string | null;
  present:    boolean;
  lookups:    number;
  all:        string | null;
  includes:   string[];
  errors:     string[];
  has_google: boolean;
  cidr_count: number;
}

export interface RuaAuthorized { rua_domain: string; authorized: boolean }

export interface Dmarc {
  record:         string | null;
  present:        boolean;
  multiple:       boolean;
  p:              DmarcPolicy | null;
  sp:             DmarcPolicy | null;
  pct:            number | null;
  rua:            string[];
  ruf:            string[];
  adkim?:         string | null;
  aspf?:          string | null;
  fo?:            string | null;
  rua_ok:         boolean;
  rua_authorized: RuaAuthorized[];
}

export interface Dkim {
  selector:        string;
  present:         boolean;
  cname:           string | null;
  seen_in_reports: boolean;
}

export interface Dns {
  ns:    string[];
  soa:   Soa;
  mx:    Mx[];
  spf:   Spf;
  dmarc: Dmarc;
  dkim:  Dkim[];
  bimi:  { record: string | null };
  web:   { a: string[]; www_cname: string | null };
}

export interface Reports {
  count_total:      number;
  count_7d:         number;
  first_at:         string | null;
  last_at:          string | null;
  last_received_at: string | null;
  orgs:             { name: string; count: number }[];
}

export interface Source {
  ip:            string;
  ptr:           string | null;
  count:         number;
  aligned:       number;
  aligned_pct:   number | null;
  dkim_pass:     number;
  spf_pass:      number;
  first_seen:    string;
  last_seen:     string;
  new:           boolean;
  in_spf:        boolean;
  via:           string[];
  is_google:     boolean;
  class:         SourceClass;
  header_from:   string[];
  envelope_from: string[];
  reporters:     string[];
  dispositions:  Record<string, number>;
}

export interface IncludeUsage { include: string; count: number }

export interface Traffic {
  window_days:    number;
  since:          string;
  days_with_data: number;
  total:          number;
  aligned:        number;
  aligned_pct:    number | null;
  unaligned:      number;
  unauthorized:   number;
  by_class:       Record<SourceClass, number>;
  includes_usage: IncludeUsage[];
  sources:        Source[];
}

export interface Finding {
  severity: Severity;
  code:     string;
  title:    string;
  detail:   string;
  action:   string;
  where:    FindingWhere;
  status:   FindingStatus;
}

export interface Recommendation {
  ready_for: DmarcPolicy | null;
  next_step: string;
  reasons:   string[];
}

export type OpenBySeverity = Partial<Record<Severity, number>>;

export interface DominioStatus {
  domain:           string;
  ticket:           string | null;
  evaluated_at:     string;
  managed_in:       ManagedIn;
  usage:            Usage;
  dns:              Dns;
  reports:          Reports;
  traffic:          Traffic;
  findings:         Finding[];
  open_by_severity: OpenBySeverity;
  recommendation:   Recommendation;
}

/** Fila de GET /dominio: resumen para las tarjetas (sin fuentes / DNS completo). */
export interface DominioSummary {
  domain:            string;
  missing:           boolean;                    // true = el evaluator todavía no lo escribió
  ticket?:           string | null;
  evaluated_at?:     string;
  managed_in?:       ManagedIn;
  usage?:            Usage;
  dns?:              { dmarc: Pick<Dmarc, "present" | "p" | "sp" | "pct" | "rua_ok" | "record"> };
  reports?:          Pick<Reports, "count_total" | "count_7d" | "last_received_at">;
  traffic?:          Omit<Traffic, "sources" | "includes_usage">;
  open_by_severity?: OpenBySeverity;
  findings_count?:   number;
  recommendation?:   Recommendation;
}

export interface HistoricoPoint {
  date:             string;          // YYYY-MM-DD (UTC del evaluator)
  evaluated_at:     string | null;
  total:            number;
  aligned_pct:      number | null;
  unauthorized:     number;
  days_with_data:   number;
  open_by_severity: OpenBySeverity;
}

// ---------- Llamadas ----------
export async function listDominios(): Promise<DominioSummary[]> {
  const res = await authFetch(`${API_BASE}/dominio`, { headers: authHeader() });
  const data = await jsonOrThrow(res, "listDominios");
  return data.domains as DominioSummary[];
}

export async function getDominio(domain: string): Promise<DominioStatus> {
  const res = await authFetch(`${API_BASE}/dominio/${encodeURIComponent(domain)}`, { headers: authHeader() });
  return jsonOrThrow(res, "getDominio");
}

export async function getHistorico(domain: string, days = 30): Promise<HistoricoPoint[]> {
  const res = await authFetch(
    `${API_BASE}/dominio/${encodeURIComponent(domain)}/historico${qsFrom({ days: String(days) })}`,
    { headers: authHeader() },
  );
  const data = await jsonOrThrow(res, "getHistorico");
  return data.items as HistoricoPoint[];
}

/** Dispara el evaluator (async). El status#latest nuevo aparece a los ~20 s. */
export async function reevaluar(domain: string): Promise<{ queued: boolean; domain: string }> {
  const res = await authFetch(`${API_BASE}/dominio/${encodeURIComponent(domain)}/reevaluar`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
  });
  return jsonOrThrow(res, "reevaluar");
}

// ---------- Metadatos de presentación (tablas de SCHEMA.md) ----------
export const SEVERITY_ORDER: Record<Severity, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

export const CLASS_META: Record<SourceClass, { label: string; fg: string; bg: string; tip: string }> = {
  ok:                     { label: "ok",                     fg: "#166534", bg: "#DCFCE7", tip: "DMARC pass siempre. Nada que hacer." },
  parcial:                { label: "parcial",                fg: "#854D0E", bg: "#FEF9C3", tip: "A veces pasa (forwarding, DKIM inestable). Mirar si supera el 1 %." },
  autorizado_sin_alinear: { label: "autorizado sin alinear", fg: "#9A3412", bg: "#FFEDD5", tip: "IP cubierta por el SPF pero DMARC falla: configurar DKIM / dominio de retorno en ese proveedor ANTES de endurecer." },
  google_sin_spf:         { label: "Google sin SPF",         fg: "#1D4ED8", bg: "#DBEAFE", tip: "Workspace manda pero no está en el SPF: sumar include:_spf.google.com (podar antes)." },
  no_autorizado:          { label: "no autorizado",          fg: "#B91C1C", bg: "#FEE2E2", tip: "IP desconocida usando el dominio: suplantación o forwarder. Es lo que quarantine/reject frena." },
};

export const WHERE_LABEL: Record<FindingWhere, string> = {
  squarespace: "Squarespace", aws: "AWS", externo: "Externo", "n/a": "—",
};

export const MANAGED_IN_LABEL: Record<ManagedIn, string> = {
  squarespace: "DNS en Squarespace", aws: "DNS en AWS (Route 53)", otro: "DNS en otro proveedor",
};
